/**
 * PsicoSalud — Sistema de notificaciones toast.
 *
 * Convive con el bloque `.notice` del sistema: el notice sigue siendo
 * el canal correcto para errores que viven dentro de un formulario
 * (validación que el profesional debe leer antes de reintentar); el
 * toast es para feedback de acciones del sistema (guardado, error
 * de red, IA procesada) que aparecen, confirman y se van solas.
 *
 * API:
 *   const { toast } = useToast();
 *   toast({ type: 'success', message: 'Paciente guardado' });
 *   toast({ type: 'error', title: 'No se pudo guardar', message: '…', duration: 6000 });
 *
 * Los toasts se apilan arriba a la derecha, entran con slide-in,
 * salen con fade-out y desaparecen solas tras `duration` ms
 * (default 4000). Si el usuario hace click en la X o en el cuerpo,
 * se descartan inmediatamente.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration: number;
}

interface ToastInput {
  type?: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: ToastItem[];
  toast: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;
function generateId(): string {
  nextId += 1;
  return `toast-${Date.now()}-${nextId}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback((input: ToastInput) => {
    const id = generateId();
    const item: ToastItem = {
      id,
      type: input.type ?? 'info',
      title: input.title,
      message: input.message,
      duration: input.duration ?? 4000,
    };
    setToasts((current) => [...current, item]);
    const timer = setTimeout(() => dismiss(id), item.duration);
    timers.current.set(id, timer);
    return id;
  }, [dismiss]);

  // Limpiar timers al desmontar el provider.
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => clearTimeout(timer));
      map.clear();
    };
  }, []);

  const value = useMemo(
    () => ({ toasts, toast, dismiss }),
    [toasts, toast, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de <ToastProvider>');
  return ctx;
}

// ============================================================
// Stack visual
// ============================================================

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div
      className="toast-stack"
      role="region"
      aria-live="polite"
      aria-label="Notificaciones del sistema"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  return (
    <div className={`toast is-${toast.type}`} role="status" data-cy={`toast-${toast.type}`}>
      <div className="toast-icon" aria-hidden="true">
        <ToastIcon type={toast.type} />
      </div>
      <div className="toast-content">
        {toast.title && <div className="toast-title">{toast.title}</div>}
        <div className="toast-message">{toast.message}</div>
      </div>
      <button
        type="button"
        className="toast-close"
        onClick={onDismiss}
        aria-label="Cerrar notificación"
      >
        ×
      </button>
    </div>
  );
}

function ToastIcon({ type }: { type: ToastType }) {
  // Glifos simples con stroke y fill coherentes con el color semántico.
  switch (type) {
    case 'success':
      return (
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M6.5 10.2l2.5 2.5L14 7.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M7 7l6 6M13 7l-6 6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10 2.5l8 13.5H2L10 2.5z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M10 8v3.5M10 14h.01"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M10 9v5M10 6h.01"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}
